import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, X, Loader2, GripVertical } from 'lucide-react';
import api from '../../api/client';
import { resolveImage } from './ImageUpload';

/**
 * Admin editor for a product's cross-sell list.
 *
 * Two responsibilities:
 *   1. Search + link — typed query hits `GET /api/products/suggest?q=...` (already
 *      part of the search-bar surface) and shows a short dropdown; picking a
 *      row appends to `product.linked_product_ids` via PUT /api/admin/products.
 *   2. Manage — the currently linked products are rendered as a compact chip
 *      list with a small Remove (×) button; the whole list can be reordered
 *      by dragging.
 *
 * Deliberately minimal: no fancy virtualisation, no bulk import — the customer
 * base tops out in the low thousands of products, so a linear scan is fine.
 */
export const LinkedRecommendationsEditor = ({ product, onSave }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [linkedDocs, setLinkedDocs] = useState([]);
  const [loadingLinked, setLoadingLinked] = useState(false);
  const searchTimer = useRef(null);
  const dragFromRef = useRef(null);

  const linkedIds = product.linked_product_ids || [];

  const loadLinked = useCallback(async () => {
    if (linkedIds.length === 0) { setLinkedDocs([]); return; }
    setLoadingLinked(true);
    try {
      const r = await api.get(`/products/accessories/${product.id}`);
      setLinkedDocs(r.data);
    } catch (e) { /* keep stale list */ }
    finally { setLoadingLinked(false); }
  }, [product.id, linkedIds.join(',')]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadLinked(); }, [loadLinked]);

  const runSearch = (q) => {
    clearTimeout(searchTimer.current);
    setQuery(q);
    if (!q.trim() || q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const r = await api.get('/products/suggest', { params: { q, limit: 8 } });
        // Filter out the current product + already-linked items.
        const excluded = new Set([product.id, ...linkedIds]);
        setResults((r.data || []).filter(p => !excluded.has(p.id)));
      } catch (e) { setResults([]); }
      finally { setSearching(false); }
    }, 220);
  };

  const addLink = async (target) => {
    const next = [...linkedIds, target.id];
    await onSave({ linked_product_ids: next });
    setQuery(''); setResults([]);
  };

  const removeLink = async (targetId) => {
    const next = linkedIds.filter(x => x !== targetId);
    await onSave({ linked_product_ids: next });
  };

  const handleDragStart = (idx) => (e) => {
    dragFromRef.current = idx;
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const handleDrop = (idx) => async (e) => {
    e.preventDefault();
    const from = dragFromRef.current;
    dragFromRef.current = null;
    if (from == null || from === idx) return;
    const next = [...linkedIds];
    const [moved] = next.splice(from, 1);
    next.splice(idx, 0, moved);
    await onSave({ linked_product_ids: next });
  };

  return (
    <div className="border-t border-neutral-200 pt-2" data-testid={`linked-recs-editor-${product.id}`}>
      <div className="text-[11px] uppercase text-[#F46A25] font-bold mb-1">Linked recommendations</div>
      <div className="text-[10px] text-neutral-500 mb-2 normal-case">
        Cross-sell shown on the PDP as "Complete your project" and in the add-to-cart nudge. Drag to reorder. Leave empty to hide.
      </div>

      {/* Search input */}
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
        <input
          type="search"
          value={query}
          onChange={e => runSearch(e.target.value)}
          placeholder="Search products to link…"
          data-testid={`linked-recs-search-${product.id}`}
          className="w-full text-xs pl-7 pr-2 py-1.5 border border-neutral-300 rounded-sm focus:outline-none focus:border-[#F46A25]"
        />
        {searching && <Loader2 className="w-3 h-3 animate-spin absolute right-2 top-1/2 -translate-y-1/2 text-[#F46A25]" />}
      </div>

      {results.length > 0 && (
        <div className="mt-1 bg-white border border-neutral-200 rounded-sm shadow-sm max-h-56 overflow-y-auto z-10 relative" data-testid={`linked-recs-search-results-${product.id}`}>
          {results.map(r => (
            <button
              key={r.id}
              type="button"
              onClick={() => addLink(r)}
              data-testid={`linked-recs-add-${r.id}`}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-left text-xs hover:bg-orange-50 border-b border-neutral-100 last:border-b-0"
            >
              <img src={resolveImage(r.image)} alt="" className="w-6 h-6 object-cover flex-shrink-0" />
              <span className="flex-1 truncate">
                <span className="font-semibold text-[#12263A]">{r.name}</span>
                <span className="text-[10px] text-neutral-500 ml-1">· {r.brand}</span>
              </span>
              <span className="text-[#F46A25] font-bold">£{Number(r.price).toFixed(2)}</span>
            </button>
          ))}
        </div>
      )}

      {/* Linked chip list */}
      <div className="mt-2 space-y-1" data-testid={`linked-recs-list-${product.id}`}>
        {loadingLinked && <div className="text-[11px] text-neutral-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>}
        {!loadingLinked && linkedDocs.length === 0 && linkedIds.length > 0 && (
          <div className="text-[11px] text-neutral-500 italic">Some linked products no longer exist.</div>
        )}
        {linkedDocs.map((r, idx) => (
          <div
            key={r.id}
            draggable
            onDragStart={handleDragStart(idx)}
            onDragOver={handleDragOver}
            onDrop={handleDrop(idx)}
            data-testid={`linked-recs-item-${r.id}`}
            className="flex items-center gap-2 px-2 py-1 bg-neutral-50 border border-neutral-200 rounded-sm text-xs group"
          >
            <GripVertical className="w-3 h-3 text-neutral-400 cursor-grab" />
            <img src={resolveImage(r.image)} alt="" className="w-5 h-5 object-cover" />
            <span className="flex-1 truncate font-semibold text-[#12263A]">{r.name}</span>
            <span className="text-neutral-500 text-[10px]">£{Number(r.price).toFixed(2)}</span>
            <button
              type="button"
              onClick={() => removeLink(r.id)}
              data-testid={`linked-recs-remove-${r.id}`}
              className="text-neutral-400 hover:text-red-600 opacity-70 group-hover:opacity-100"
              aria-label="Remove link"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LinkedRecommendationsEditor;
